import {createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState} from 'react'

interface NestableActionMenuContextProps {
  onCloseStack: () => void
  /**
   * Register whether a nested select panel is open or not.
   * @param id unique identifier for the nested select panel
   * @param open whether the nested select panel is open (true) or closed (false)
   */
  setNestedSelectPanelOpenState: (id: string, open: boolean) => void
  /**
   * Whether there are any nested select panels that are currently open.
   */
  anyNestedSelectPanelsOpen: boolean
}

export const NestableActionMenuContext = createContext<NestableActionMenuContextProps | null>(null)

type NestableActionMenuProviderProps = PropsWithChildren<Pick<NestableActionMenuContextProps, 'onCloseStack'>>

export const NestableActionMenuProvider = ({children, onCloseStack}: NestableActionMenuProviderProps) => {
  const [nestedSelectPanelOpenStates, setNestedSelectPanelOpenStates] = useState(new Map<string, boolean>())
  const anyNestedSelectPanelsOpen = Array.from(nestedSelectPanelOpenStates.values()).some(open => open)

  const setNestedSelectPanelOpenState = useCallback(
    (id: string, open: boolean) => {
      const newValue = new Map<string, boolean>(Array.from(nestedSelectPanelOpenStates.entries()))
      newValue.set(id, open)
      setNestedSelectPanelOpenStates(newValue)
    },
    [nestedSelectPanelOpenStates],
  )

  const value = useMemo(
    () => ({onCloseStack, setNestedSelectPanelOpenState, anyNestedSelectPanelsOpen}),
    [onCloseStack, setNestedSelectPanelOpenState, anyNestedSelectPanelsOpen],
  )

  return <NestableActionMenuContext.Provider value={value}>{children}</NestableActionMenuContext.Provider>
}

export const useNestableActionMenu = () => {
  return useContext(NestableActionMenuContext)
}

try{ NestableActionMenuContext.displayName ||= 'NestableActionMenuContext' } catch {}
try{ NestableActionMenuProvider.displayName ||= 'NestableActionMenuProvider' } catch {}